import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaEye, FaEdit, FaTrash } from "react-icons/fa";
import { FaAddressBook, FaSitemap } from "react-icons/fa";
import ViewEmployee from "./view_employee";
import ConfirmModal from "./delete_modal";
import OrgChart from "./org_chart";
import Dropdown from "../../components/modal/dropdown";
import Can from "../common/can";
import { toast } from "react-toastify";
import "./employee.css";
import { useAuth } from "../context/authcontext";
import Pagination from "../common/pagination";
import {
  getEmployees, deleteEmployee as deleteEmployeeAPI, getDepartments, getDesignations, getEmployeeById
} from "../services/employeeservice";
import {
  EmployeeStatusMeta,
  EmployeeStatusOptions,
  EmployeeStatusFilterOptions,
  getEmployeeStatusLabel,
  getEmployeeStatusClass,
} from "../../constants/constants";

const PAGE_SIZE = 20;

// Boundary point between the outside world and internal filter state.
// Internal state is always the sentinel "all" or a numeric EmployeeStatus id
// (see constants.js) — but some existing links (e.g. Dashboard quick-links)
// still deep-link here with a *label*, like "?status=Active". Accepting
// either a numeric id or a label (case-insensitive) here means those links
// keep working without the rest of the component ever touching a label.
const parseStatusParam = (raw) => {
  if (!raw || raw.toLowerCase() === "all") return "all";
  const num = Number(raw);
  if (!Number.isNaN(num) && EmployeeStatusMeta[num]) return num;
  const match = EmployeeStatusOptions.find(
    (o) => o.label.toLowerCase() === raw.toLowerCase()
  );
  return match ? match.value : "all";
};

const Employee = () => {
  const { user, hasPermission, hasAnyPermission, hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  const canShowActions = isAdmin || hasAnyPermission(["employee.view", "employee.view_others", "employee.update", "employee.delete"]);
  const location = useLocation();
  const navigate = useNavigate();
  const [employees, setEmployees]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [page, setPage]               = useState(1);
  const [totalCount, setTotal]        = useState(0);

  const [search, setSearch]           = useState("");
  const [statusFilter, setStatusFilter] = useState(() => {
    const params = new URLSearchParams(location.search);
    return parseStatusParam(params.get("status"));
  });
  const [deptFilter, setDeptFilter]     = useState("All");
  const [deptOptions, setDeptOptions]   = useState(["All"]);
  const [desigFilter, setDesigFilter]   = useState("All");
  const [desigOptions, setDesigOptions] = useState(["All"]);
  const [activeTab, setActiveTab]       = useState("Directory");

  const [deleteEmployee, setDeleteEmployee] = useState(null);
  const [viewEmployee, setViewEmployee] = useState(null);
  const [newPasswordData, setNewPasswordData] = useState(null);

  // Guards against out-of-order fetch responses (e.g. an unfiltered request
  // that resolves after a filtered one overwriting it with stale data)
  const fetchIdRef = useRef(0);

  const fetchEmployees = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    try {
      setLoading(true);
      const params = { page, page_size: PAGE_SIZE };
      if (search)                         params.search = search;
      if (statusFilter !== "all")         params.status = statusFilter;
      if (deptFilter !== "All")           params.department_name = deptFilter;
      if (desigFilter !== "All")          params.designation_name = desigFilter;
      const res = await getEmployees(params);
      if (fetchId !== fetchIdRef.current) return; // a newer request already superseded this one
      const data = res.data || res;
      const meta = res.meta || {};
      const rawEmployees = Array.isArray(data) ? data : data.results || [];
      setEmployees(rawEmployees);
      setTotal(meta.count ?? data.count ?? (Array.isArray(data) ? data.length : 0));
    } catch {
      if (fetchId === fetchIdRef.current) toast.error("Failed to load employees");
    } finally {
      if (fetchId === fetchIdRef.current) setLoading(false);
    }
  }, [page, search, statusFilter, deptFilter, desigFilter]);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);
  useEffect(() => { setPage(1); }, [search, statusFilter, deptFilter, desigFilter]);

  /* Pick up a ?status= query param (e.g. from Dashboard quick-links) and
     pre-apply it to the status filter dropdown. Accepts either a numeric
     EmployeeStatus id or a label (e.g. "Active") — see parseStatusParam. */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get("status");
    if (status) {
      setStatusFilter(parseStatusParam(status));
    }
  }, [location.search]);

  /* Pick up a temporary-password payload handed back after redirecting from
     the Add Employee page, then clear it from history state so a refresh
     or back-navigation doesn't re-show it. */
  useEffect(() => {
    if (location.state?.newPasswordData) {
      setNewPasswordData(location.state.newPasswordData);
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, location.search, navigate]);

  useEffect(() => {
    getDepartments().then((data) => {
      setDeptOptions(["All", ...data.map((d) => d.name)]);
    }).catch(() => {});
    getDesignations().then((data) => {
      setDesigOptions(["All", ...data.map((d) => d.name)]);
    }).catch(() => {});
  }, []);

  const confirmDelete = async (password) => {
    try {
      await deleteEmployeeAPI(deleteEmployee.id, password);
      toast.success("Employee deleted successfully");
      await fetchEmployees();
      setDeleteEmployee(null);
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.detail || err.response?.data?.error || "Failed to delete employee: Incorrect password or permission denied";
      toast.error(msg);
      throw new Error(msg);
    }
  };

  const handleViewClick = async (e) => {
    try {
      setLoading(true);
      const res = await getEmployeeById(e.id);
      setViewEmployee(res.data || res);
    } catch {
      toast.error("Failed to load employee details");
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <div className="container">
      <div className="page-header">
        <h2>Employees</h2>
        {activeTab === "Directory" && (
          <Can perm="employee.create">
            <button className="primary-btn" onClick={() => navigate("/employee/add")}>
              Add New Employee
            </button>
          </Can>
        )}
      </div>

      <div className="account-tabs" style={{ marginBottom: 24 }}>
        <button className={`account-tab ${activeTab === "Directory" ? "active" : ""}`} onClick={() => setActiveTab("Directory")}>
            <FaAddressBook />  Directory
        </button>
        {hasPermission("employee.view_org_chart") && (
          <button className={`account-tab ${activeTab === "Org Chart" ? "active" : ""}`} onClick={() => setActiveTab("Org Chart")}>
         <FaSitemap />  Org Chart
          </button>
        )}
      </div>

      {activeTab === "Directory" && (
        <>
          <div className="filters-card single">
            <input
              className="search-input"
              placeholder="🔍 Search by Emp ID, Name, Email or Phone"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Dropdown options={EmployeeStatusFilterOptions} value={statusFilter} onChange={setStatusFilter} placeholder="Status" />
            <Dropdown options={deptOptions} value={deptFilter} onChange={setDeptFilter} placeholder="Department" />
            <Dropdown options={desigOptions} value={desigFilter} onChange={setDesigFilter} placeholder="Designation" />
          </div>

      {loading ? (
        <div className="empty-state"><p>Loading Employees…</p></div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Emp ID</th>
              <th>Name</th>
              <th>Department</th>
              <th>Designation</th>
              <th>Status</th>
              {canShowActions && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {employees.length === 0 ? (
              <tr><td colSpan={canShowActions ? "6" : "5"}>
                <div className="empty-state">
                  <img src="/images/noemployee.svg" alt="No employees" className="empty-img" />
                  <p>No Employees Found</p>
                </div>
              </td></tr>
            ) : (
              employees.map((e) => {
                const canViewProfile = isAdmin || hasAnyPermission(["employee.view", "employee.view_others"]) || user?.employee_id === e.id || user?.id === e.user_id;
                return (
                <tr key={e.id}>
                  <td>{e.emp_id}</td>
                  <td>{e.full_name}</td>
                  <td>{e.department_name}</td>
                  <td>{e.designation_name}</td>
                  <td>
                    <span className={`emp-status ${getEmployeeStatusClass(e.status)}`}>
                      {getEmployeeStatusLabel(e.status)}
                    </span>
                  </td>
                  {canShowActions && (
                    <td className="action-icons">
                      {canViewProfile && (
                        <button className="icon-btn view" title="View Profile" onClick={() => handleViewClick(e)}><FaEye /></button>
                      )}
                      <Can perm="employee.update">
                        <button className="icon-btn edit" title="Edit Employee Profile" onClick={() => navigate(`/employee/edit/${e.id}`)}><FaEdit /></button>
                      </Can>
                      <Can perm="employee.delete">
                        <button className="icon-btn delete" title="Delete Employee" onClick={() => setDeleteEmployee(e)}><FaTrash /></button>
                      </Can>
                    </td>
                  )}
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      )}

          <Pagination page={page} totalPages={totalPages} setPage={setPage} />
        </>
      )}

      {activeTab === "Org Chart" && hasPermission("employee.view_org_chart") && (
        <OrgChart />
      )}

      {viewEmployee && (
        <ViewEmployee employee={viewEmployee} onClose={() => setViewEmployee(null)} onRefresh={fetchEmployees} />
      )}

      {deleteEmployee && (
        <ConfirmModal
          title="Delete Employee"
          message={`Delete ${deleteEmployee.full_name}?`}
          confirmText="Delete"
          type="danger"
          requirePassword={true}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteEmployee(null)}
        />
      )}

      {newPasswordData && (
        <div className="modal-overlay">
          <div className="modal-content premium-password-modal" style={{ maxWidth: 400, textAlign: "center", padding: "28px" }}>
            <div className="password-modal-icon">🔐</div>
            <h3 style={{ marginBottom: "16px", color: "var(--text-main)" }}>Employee Created!</h3>
            <p style={{ color: "var(--text-muted)", marginBottom: "8px" }}>
              Please copy the temporary password for <strong>{newPasswordData.name}</strong>:
            </p>
            <div className="password-display-premium">
              {newPasswordData.password}
            </div>
            <button className="primary-btn" style={{ width: "100%" }} onClick={() => setNewPasswordData(null)}>
              Done
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default Employee;